"use client";

import { useEffect, useRef, useState } from "react";
import "./OrgStatePreviewer.css";

/* -------------------------------------------------------------------------- */
/* Types                                                                        */
/* -------------------------------------------------------------------------- */

export type OrgStatePresetTone =
  | "complete"
  | "gray"
  | "yellow"
  | "red"
  | "mixed";

/** A single state preset entry shown in the flyout menu. */
export interface OrgStatePreset {
  id: string;
  label: string;
  description: string;
  /** Controls the color of the dot indicator. */
  tone: OrgStatePresetTone;
}

export interface OrgStatePreviewerProps {
  /** List of demo-state presets to display. */
  presets: OrgStatePreset[];
  /** ID of the currently active preset. */
  activePresetId?: string;
  /** Called when the user clicks a preset row. */
  onApply: (presetId: string) => void;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Fixed-bottom-right developer tool: "Demo states" flyout that lets you
 * switch the application into a predefined org state.  Entirely props-driven —
 * no context or data module dependencies.
 */
export function OrgStatePreviewer({
  presets,
  activePresetId,
  onApply,
}: OrgStatePreviewerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  /* Close on outside click / Escape */
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="org-preview" ref={wrapRef} data-open={open || undefined}>
      <button
        type="button"
        className="org-preview-toggle"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        title="Demo states"
      >
        {/* Flask icon */}
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M9 2v6L4 18a2 2 0 0 0 1.7 3h12.6a2 2 0 0 0 1.7-3L15 8V2" />
          <path d="M8 2h8" />
        </svg>
        Demo states
      </button>

      {open && (
        <div className="org-preview-menu" role="menu">
          <div className="org-preview-menu-head">
            <span className="org-preview-menu-title">Preview org states</span>
            <span className="org-preview-menu-sub">
              Click to apply. Edits stay until you switch.
            </span>
          </div>

          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitem"
              className={`org-preview-item${activePresetId === p.id ? " active" : ""}`}
              onClick={() => {
                onApply(p.id);
                setOpen(false);
              }}
            >
              <span
                className={`org-preview-dot tone-${p.tone}`}
                aria-hidden="true"
              />
              <span className="org-preview-text">
                <span className="org-preview-label">{p.label}</span>
                <span className="org-preview-desc">{p.description}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
